/**
 * Pure helpers + label constants used by the field-editor surface.
 * No JSX, no React state — keeps the React components testable.
 */
import { nanoid } from 'nanoid'
import { ruleSpec } from '@/lib/form-engine'
import type { FieldType, ValidationRule } from '../api/form-builder-api'

export const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'متن',
  textarea: 'متن بلند',
  number: 'عدد',
  date: 'تاریخ',
  time: 'زمان',
  datetime: 'تاریخ و زمان',
  single_select: 'انتخاب تکی',
  multi_select: 'انتخاب چندگانه',
  boolean: 'بله/خیر',
  file: 'فایل',
  image: 'تصویر',
  calculated: 'محاسباتی',
  repeatable_array: 'آرایه تکرارپذیر',
}

export const SUB_FIELD_TYPES: Array<{ value: FieldType; label: string }> = [
  { value: 'text', label: 'متن' },
  { value: 'textarea', label: 'متن بلند' },
  { value: 'number', label: 'عدد' },
  { value: 'date', label: 'تاریخ' },
  { value: 'time', label: 'زمان' },
  { value: 'single_select', label: 'انتخاب تکی' },
  { value: 'multi_select', label: 'انتخاب چندگانه' },
  { value: 'boolean', label: 'بله/خیر' },
  { value: 'file', label: 'فایل' },
  { value: 'image', label: 'تصویر' },
]

/**
 * Reads legacy/current LocalizedString OR plain string into a plain string.
 * Historical bug: boolean true_label/false_label were stored as `{fa: '...'}`
 * LocalizedString while the schema/runtime treats them as plain strings.
 */
export function readLocalized(v: unknown): string | undefined {
  if (v == null) return undefined
  if (typeof v === 'string') return v
  if (typeof v === 'object') return (v as { fa?: string }).fa
  return String(v)
}

/**
 * Sanitize a free-text label into a valid `field_key`. Only ASCII letters,
 * digits, and underscore are allowed. Spaces become underscores; everything
 * else is stripped. Lowercased so field_keys are case-insensitive and
 * JSON-stable.
 */
export function sanitizeFieldKey(raw: string): string {
  return raw
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .slice(0, 48)
}

export function sanitizeOptionKey(raw: string): string {
  return raw
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .slice(0, 48)
}

export function makeNewValidationRule(fieldType: FieldType, ruleType: string): ValidationRule {
  const spec = ruleSpec(fieldType, ruleType)
  return {
    rule_id: nanoid(),
    rule_type: ruleType,
    severity: spec?.defaultSeverity ?? 'blocking',
    is_enabled: true,
    rule_config: {},
  }
}

export const VALIDATION_ACTION_LABELS: Record<
  string,
  { label: string; tone: 'amber' | 'sky' | 'rose' }
> = {
  require:           { label: 'این فیلد را اجباری می‌کند',           tone: 'amber' },
  unrequire:         { label: 'این فیلد را اختیاری می‌کند',          tone: 'sky'   },
  show:              { label: 'این فیلد را نمایان می‌کند',            tone: 'sky'   },
  hide:              { label: 'این فیلد را پنهان می‌کند',             tone: 'rose'  },
  enable:            { label: 'این فیلد را فعال می‌کند',              tone: 'sky'   },
  disable:           { label: 'این فیلد را غیرفعال می‌کند',           tone: 'rose'  },
  filter_options:    { label: 'گزینه‌های این فیلد را فیلتر می‌کند',   tone: 'sky'   },
  set_default_value: { label: 'مقدار پیش‌فرض این فیلد را تنظیم می‌کند', tone: 'sky'   },
}
